import type { CoverageGroup } from './coverage.types';
import { date, relativeDays, type CoverageTone } from './coverageFormat';
import { daysUntil, termProgress } from './coverageDates';

/**
 * What the tile says about a group, as data rather than as nested JSX.
 *
 * Keeping the wording here means each of the twelve states is one branch with
 * one test, instead of a stack of conditionals inside the render -- which is
 * how the prototype ended up with states it could describe but never reach.
 */
export interface CoverageNarrative {
  readonly headline: string;
  readonly detail?: string;
  readonly tone: CoverageTone;
  /** 0-1 for the term bar; omitted where a bar would mean nothing. */
  readonly progress?: number;
}

const pluralDays = (days: number) => `${days} ${days === 1 ? 'day' : 'days'}`;

export function describeGroup(
  group: CoverageGroup,
  now: string | number | Date = Date.now()
): CoverageNarrative {
  const { current, upcoming, lapsed, quotes, state } = group;
  const quote = quotes.find(
    (line) => line.status === 'quoteReady' || line.status === 'quoteBlocked'
  );

  if (state === 'quoteReady') {
    return {
      headline: 'Ready to bind — nothing outstanding',
      detail: `The premium is locked until ${date(
        quote?.expiresOn
      )}. Nothing is covered until you bind.`,
      tone: 'caution',
      // No bar. A quote has no term running, and a full one read as 100% elapsed on
      // something that is not coverage at all.
    };
  }

  if (state === 'quoteBlocked') {
    return {
      headline: 'Waiting on outstanding items',
      detail:
        'A few details are still needed before this quote can be bound. Your agent can tell you which.',
      tone: 'caution',
    };
  }

  if (state === 'quoteExpired') {
    return {
      headline: `This quote expired ${date(quotes[0]?.expiresOn)}`,
      detail:
        'Premiums are priced against a scan at the time of quoting, so a new indication needs a fresh one.',
      tone: 'neutral',
    };
  }

  if (state === 'awaitingPayment') {
    return {
      headline: 'Bound, but not yet complete',
      detail: describeOutstanding(current?.isAppSigned, current?.isPaid),
      tone: 'caution',
    };
  }

  if (state === 'preInception') {
    const days = daysUntil(current?.effectiveDate, now);

    return {
      headline: `Coverage begins ${date(current?.effectiveDate)}`,
      detail: `Nothing to do until then${
        days === undefined ? '' : `, ${relativeDays(days)}`
      }. A claim can only be reported for incidents on or after that date.`,
      tone: 'neutral',
      progress: 0,
    };
  }

  if (state === 'cancelPending') {
    return {
      headline: `Cancellation pending — cover ends ${date(current?.termEnd)}`,
      detail:
        'This policy is still in force until that date. Your agent can tell you what is needed to stop the cancellation.',
      tone: 'critical',
      progress: termProgress(current?.effectiveDate, current?.termEnd, now),
    };
  }

  if (state === 'renewalBound') {
    return {
      headline: 'Your renewal is already bound',
      detail: `Coverage continues without a gap through ${date(
        upcoming?.termEnd
      )}.`,
      tone: 'positive',
      progress: termProgress(current?.effectiveDate, current?.termEnd, now),
    };
  }

  if (state === 'renewalQuoted' || state === 'renewalDue') {
    const days = daysUntil(current?.termEnd, now);

    return {
      headline:
        days === undefined
          ? 'Renewal is coming up'
          : `Renews in ${pluralDays(days)}`,
      detail:
        state === 'renewalQuoted'
          ? 'A renewal quote is ready for your review below.'
          : 'Your renewal quote will appear here as soon as it is ready.',
      tone: 'caution',
      progress: termProgress(current?.effectiveDate, current?.termEnd, now),
    };
  }

  if (state === 'inForce') {
    const days = daysUntil(current?.termEnd, now);

    return {
      headline: `In force · ${date(current?.effectiveDate)} — ${date(
        current?.termEnd
      )}`,
      detail:
        days === undefined
          ? undefined
          : `${pluralDays(
              days
            )} of coverage remaining. Mid-term changes go through an endorsement.`,
      tone: 'positive',
      progress: termProgress(current?.effectiveDate, current?.termEnd, now),
    };
  }

  if (state === 'lapsed') {
    return {
      headline: `Coverage ended ${date(lapsed?.termEnd)}`,
      detail:
        'You have no cover in place for this product. Claims for incidents during the term can still be reported.',
      tone: 'critical',
    };
  }

  if (state === 'cancelled') {
    return {
      headline: `Cancelled ${date(lapsed?.termEnd)}`,
      detail:
        'Claims for incidents while it was in force can still be reported.',
      tone: 'critical',
    };
  }

  // The pill already reads "Status unavailable"; repeating it here would spend
  // the headline saying nothing the reader has not just read.
  return {
    headline: 'We can\u2019t confirm where this stands right now',
    detail:
      'The policy details below are accurate. Your agent can confirm its current status.',
    tone: 'neutral',
  };
}

function describeOutstanding(
  isAppSigned: boolean | undefined,
  isPaid: boolean | undefined
): string {
  const outstanding = [
    isAppSigned === false ? 'signed' : undefined,
    isPaid === false ? 'paid' : undefined,
  ].filter(Boolean);

  if (outstanding.length === 0) {
    return 'Coverage starts once everything outstanding is settled.';
  }

  return `Coverage does not start until the application is ${outstanding.join(
    ' and the premium '
  )}.`;
}
